import { localityQuery } from 'location/locality'
import { useLocality } from 'location/LocalityProvider'
import { useRouter } from 'next/router'
import type { FC, ReactNode } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { pagesDataAPI } from '../../axios/axios'
import type { QueryKeyENUM } from '../../components/atoms/Filters/ApplyFilters.model'
import { FilterTypeENUM } from '../../components/Filters/0_FilteraCollection/Filters.model'
import type { ItemType } from '../../components/Filters/ApplyFilters.model'
import { useCatalogueFilterQuery } from '../../components/Filters/useCatalogueFilterQuery'
import {
  clearFilters,
  setFiltersAction,
} from '../../store/reducers/filter/filterReducer'
import type { RootState } from '../../store/store'
import { BarContentContainer } from './components/BarContentContainer/BarContentContainer'
import { Mapbox } from './components/mapbox/Mapbox'
import type { IMapProps, ObjectsMapType } from './Map.model'
import { objectsNameENUM } from './Map.model'
import {
  BreadcrumbItem,
  MapWrapp,
  SBreadcrumbsFilter,
  SCardContainer,
} from './style/Map.styled'

export type MenuFilterType = objectsNameENUM.programs | objectsNameENUM.events

const initialTypeMenu: Record<
  MenuFilterType,
  FilterTypeENUM.eventsFilters | FilterTypeENUM.programsFilters
> = {
  events: FilterTypeENUM.eventsFilters,
  programs: FilterTypeENUM.programsFilters,
}
const programFilterMode: Partial<Record<objectsNameENUM, boolean>> = {
  programs: true,
  events: false,
}

const Map: FC<IMapProps> = ({ mapData }) => {
  const router = useRouter()
  const { locality } = useLocality()
  const dispatch = useDispatch()

  const selectedType = router.query.type as objectsNameENUM | undefined
  const panelRequestId = useRef(0)
  const [visiblePanel, setVisiblePanel] = useState(false)
  /** TODO any */
  const [dataForPanel, setDataForPanel] = useState<any>([])

  const { filters } = useSelector((state: RootState) => state)
  const currentFilters =
    selectedType === objectsNameENUM.programs
      ? FilterTypeENUM.programsFilters
      : FilterTypeENUM.eventsFilters
  const syncFilter = useCallback(
    (key: QueryKeyENUM, item: ItemType | null) => {
      dispatch(setFiltersAction({ key: currentFilters, filterKey: key, item }))
    },
    [dispatch, currentFilters],
  )
  useCatalogueFilterQuery(
    selectedType ? programFilterMode[selectedType] : undefined,
    filters[currentFilters],
    syncFilter,
  )

  const activeFilterMenu = useMemo(
    () => initialTypeMenu[router.query.type as MenuFilterType],
    [router.query.type],
  )

  const handleSetVisiblePanel = useCallback((visible: boolean) => {
    setVisiblePanel(visible)
  }, [])

  const setTypeObject = useCallback(
    (type: objectsNameENUM) => {
      router.push({
        pathname: '/map',
        query: {
          ...localityQuery(locality),
          type,
        },
      })
      dispatch(clearFilters())
    },
    [dispatch, router, locality],
  )

  const afterClearFilterCallback = useCallback(() => {
    router.push({
      pathname: router.pathname,
      query: {
        ...localityQuery(locality),
        type: router.query.type,
      },
    })
  }, [router, locality])

  const callbackData = useCallback(
    async (data: ObjectsMapType[]) => {
      const requestId = panelRequestId.current + 1
      panelRequestId.current = requestId
      const params = new URLSearchParams(window.location.search)
      const queryType = params.get('type')

      switch (queryType) {
        case objectsNameENUM.events: {
          const objData = await pagesDataAPI.getMultipleEvents(
            data.map((object) => object.obj_id),
          )
          if (objData && requestId === panelRequestId.current) {
            setDataForPanel(objData)
            handleSetVisiblePanel(true)
          }
          break
        }

        case objectsNameENUM.organizations: {
          const objData = await pagesDataAPI.getMultipleUnits(
            data.map((object) => object.obj_id),
          )
          if (objData && requestId === panelRequestId.current) {
            setDataForPanel(objData)
            handleSetVisiblePanel(true)
          }
          break
        }

        case objectsNameENUM.programs: {
          const objData = await pagesDataAPI.getMultiplePrograms(
            data.map((object) => object.obj_id),
          )
          if (objData && requestId === panelRequestId.current) {
            setDataForPanel(objData)
            handleSetVisiblePanel(true)
          }
          break
        }

        default:
          break
      }
    },
    [handleSetVisiblePanel],
  )

  const breadcrumbFilters = useMemo(() => {
    const crumbs: ReactNode[] = []
    if (
      FilterTypeENUM.eventsFilters in filters &&
      selectedType === objectsNameENUM.events
    ) {
      const keys = Object.keys(filters[FilterTypeENUM.eventsFilters])
      if (keys.length) {
        keys.forEach((key) => {
          const obj = filters[FilterTypeENUM.eventsFilters][key]
          if (
            obj &&
            router.query[key] !== undefined &&
            String(obj.value) === String(router.query[key])
          ) {
            if (obj.label === 'date_from' || obj.label === 'date_to') {
              crumbs.push(<BreadcrumbItem>{obj.value}</BreadcrumbItem>)
            } else crumbs.push(<BreadcrumbItem>{obj.name}</BreadcrumbItem>)
          }
        })
      }
    }
    if (
      FilterTypeENUM.programsFilters in filters &&
      selectedType === objectsNameENUM.programs
    ) {
      const keys = Object.keys(filters[FilterTypeENUM.programsFilters])
      if (keys.length) {
        keys.forEach((key) => {
          const obj = filters[FilterTypeENUM.programsFilters][key]
          if (
            obj &&
            router.query[key] !== undefined &&
            String(obj.value) === String(router.query[key])
          ) {
            crumbs.push(<BreadcrumbItem>{obj.name}</BreadcrumbItem>)
          }
        })
      }
    }
    return crumbs
  }, [filters, selectedType, router.query])

  useEffect(() => {
    panelRequestId.current += 1
    setVisiblePanel(false)
    setDataForPanel([])
  }, [router.asPath])

  return (
    <MapWrapp>
      <SBreadcrumbsFilter>{breadcrumbFilters}</SBreadcrumbsFilter>
      <SCardContainer>
        <BarContentContainer
          visiblePanel={visiblePanel}
          handleSetVisiblePanel={handleSetVisiblePanel}
          dataForPanel={dataForPanel}
          selectedType={selectedType}
          activeFilterMenu={activeFilterMenu}
          afterClearFilterCallback={afterClearFilterCallback}
          setTypeObject={setTypeObject}
        />
      </SCardContainer>
      <Mapbox callbackData={callbackData} featureCollection={mapData} />
    </MapWrapp>
  )
}

export default React.memo(Map)
